import { useEffect, useRef, useState, type CSSProperties } from "react";
import { api } from "./api.js";
import { logError } from "./logger.js";

export interface ResolvedAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  district: string;
  pincode: string;
  lat: number;
  lng: number;
}

interface Suggestion {
  placeId: string;
  text: string;
}

const dropdown: CSSProperties = {
  position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20,
  background: "var(--color-surface-0)", border: "1.5px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", marginTop: 4, maxHeight: 220,
  overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
};
const item: CSSProperties = {
  padding: "8px 12px", fontSize: 13, cursor: "pointer", borderBottom: "1px solid var(--color-surface-100)",
};

export function AddressAutocompleteInput({
  value, onChange, onResolve, placeholder, style,
}: {
  value: string;
  onChange: (value: string) => void;
  onResolve: (address: ResolvedAddress) => void;
  placeholder?: string;
  style?: CSSProperties;
}) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const sessionTokenRef = useRef<string>(crypto.randomUUID());
  const unavailableRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const fetchSuggestions = (input: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (unavailableRef.current || input.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get<{ suggestions: Suggestion[] }>("/address/autocomplete", {
          params: { input, sessionToken: sessionTokenRef.current },
        });
        setSuggestions(data.suggestions);
        setOpen(data.suggestions.length > 0);
      } catch (err) {
        if (axiosStatus(err) === 503) unavailableRef.current = true;
        setSuggestions([]);
      }
    }, 300);
  };

  const handleSelect = async (s: Suggestion) => {
    try {
      const { data } = await api.get<ResolvedAddress>("/address/resolve", {
        params: { placeId: s.placeId, sessionToken: sessionTokenRef.current },
      });
      onResolve(data);
    } catch (err) {
      logError("addressAutocomplete.handleSelect", err);
      onChange(s.text);
    } finally {
      setOpen(false);
      setSuggestions([]);
      sessionTokenRef.current = crypto.randomUUID();
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        value={value}
        placeholder={placeholder}
        style={style}
        onChange={(e) => {
          onChange(e.target.value);
          fetchSuggestions(e.target.value);
        }}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && suggestions.length > 0 && (
        <div style={dropdown}>
          {suggestions.map((s) => (
            <div
              key={s.placeId}
              style={item}
              data-testid="address-suggestion"
              onMouseDown={(e) => { e.preventDefault(); void handleSelect(s); }}
            >
              {s.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function axiosStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}
